import React, {
  FC,
  useEffect,
  useState,
  memo,
  useMemo,
  useCallback,
} from 'react';
import { getActions, withGlobal } from '../../global';

import type { ApiChat, ApiMessage } from '../../api/types';
import { MAIN_THREAD_ID } from '../../api/types';
import type {
  MessageListType,
  ActiveEmojiInteraction,
} from '../../global/types';
import {
  AnimationLevel,
  ERoutes,
  LeftColumnContent,
  MiddleColumnContent,
  ThemeKey,
  ThreadId,
} from '../../types';

import {
  MIN_SCREEN_WIDTH_FOR_STATIC_LEFT_COLUMN,
  MOBILE_SCREEN_MAX_WIDTH,
  MIN_SCREEN_WIDTH_FOR_STATIC_RIGHT_COLUMN,
  SAFE_SCREEN_WIDTH_FOR_STATIC_RIGHT_COLUMN,
  SAFE_SCREEN_WIDTH_FOR_CHAT_INFO,
  ANIMATION_LEVEL_MAX,
  ANIMATION_END_DELAY,
  SUPPORTED_IMAGE_CONTENT_TYPES,
  TMP_CHAT_ID,
  SUPPORT_USER_ID,
  AI_BOT_PHOENIX,
  AI_BOT_CANCER,
  AI_BOT_BUSINESS,
} from '../../config';
import { MASK_IMAGE_DISABLED } from '../../util/windowEnvironment';
import { DropAreaState } from './composer/DropArea';
import {
  selectChat,
  selectChatBot,
  selectCurrentMessageList,
  selectTabState,
  selectCurrentTextSearch,
  selectIsRightColumnShown,
  selectPinnedIds,
  selectReplyingToId,
  selectTheme,
  selectChatMessage,
  selectCurrentMessageIds,
  selectChatScheduledMessages,
  selectChatMessages,
} from '../../global/selectors';
import { isChatChannel, isUserId } from '../../global/helpers';
import calculateMiddleFooterTransforms from './helpers/calculateMiddleFooterTransforms';
import captureEscKeyListener from '../../util/captureEscKeyListener';
import useCustomBackground from '../../hooks/useCustomBackground';
import useWindowSize from '../../hooks/useWindowSize';
import usePrevDuringAnimation from '../../hooks/usePrevDuringAnimation';
import useSyncEffect from '../../hooks/useSyncEffect';
import useAppLayout from '../../hooks/useAppLayout';
import usePinnedMessage from './hooks/usePinnedMessage';
import Transition from '../ui/Transition';
import MiddleHeader from './MiddleHeader';
import MessageList from './MessageList';
import FloatingActionButtons from './FloatingActionButtons';
import Composer from './composer/Composer';
import Button from '../ui/Button';
import MobileSearch from './MobileSearch.async';
import MessageSelectToolbar from './MessageSelectToolbar.async';
import SeenByModal from '../common/SeenByModal.async';
import ReactorListModal from './ReactorListModal.async';
import MessageLanguageModal from './MessageLanguageModal.async';
import { useTranslation } from 'react-i18next';
import IconSvg from '../ui/IconSvg';
import classNames from 'classnames';
import EmojiInteractionAnimation from './EmojiInteractionAnimation';
import { Helmet } from 'react-helmet';
import './MiddleColumn.scss';
import styles from './MiddleColumn.module.scss';
import { useNavigate } from 'react-router';
import useEffectWithPrevDeps from '../../hooks/useEffectWithPrevDeps';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import useBrowserOnline from '../../hooks/useBrowserOnline';

interface OwnProps {
  chatId: string;
  threadId?: ThreadId;
  isMobile?: boolean;
  isReady: boolean;
  prevMiddleScreen?: MiddleColumnContent;
  renderingMessageListType: MessageListType;
  canPost: boolean;
  isComments?: boolean;
  withBottomShift?: boolean;
  renderingShouldSendJoinRequest?: boolean;
  withExtraShift?: boolean;
  isSelectModeActive?: boolean;
  isPinnedMessageList?: boolean;
  isMessagingDisabled?: boolean;
  composerRestrictionMessage?: string;
  renderingCanRestartBot?: boolean;
  renderingCanStartBot?: boolean;
  prevTransitionKey?: number;
  currentTransitionKey: number;
  shouldSkipHistoryAnimations?: boolean;
  animationLevel: AnimationLevel;
  handleOpenEnd?: (e: any) => void;
  handleSlideStop?: () => void;
}

type StateProps = {
  chat?: ApiChat;
  replyingToId?: number;
  isPrivate?: boolean;
  isScheduledMessageList?: boolean;
  hasPinned?: boolean;
  hasAudioPlayer?: boolean;
  pinnedMessagesCount?: number;
  theme: ThemeKey;
  customBackground?: string;
  backgroundColor?: string;
  patternColor?: string;
  isLeftColumnShown?: boolean;
  isRightColumnShown?: boolean;
  isBackgroundBlurred?: boolean;
  hasCurrentTextSearch?: boolean;
  isSeenByModalOpen: boolean;
  isReactorListModalOpen: boolean;
  isGiftPremiumModalOpen?: boolean;
  isMessageLanguageModalOpen?: boolean;
  isChannel?: boolean;
  areChatSettingsLoaded?: boolean;
  activeEmojiInteractions?: ActiveEmojiInteraction[];
  lastSyncTime?: number;
  pinnedIds?: number[];
  isBot?: boolean;
  messageIds?: number[];
  messagesById?: Record<number, ApiMessage>;
};

function isImage(item: DataTransferItem) {
  return (
    item.kind === 'file' &&
    item.type &&
    SUPPORTED_IMAGE_CONTENT_TYPES.has(item.type)
  );
}

const LAYER_ANIMATION_DURATION_MS = 450 + ANIMATION_END_DELAY;

const MessagesLayout: FC<OwnProps & StateProps> = ({
  chat,
  chatId,
  threadId,
  isMobile,
  isReady,
  renderingMessageListType,
  isPrivate,
  hasPinned,
  hasAudioPlayer,
  pinnedMessagesCount,
  customBackground,
  theme,
  backgroundColor,
  isLeftColumnShown,
  isRightColumnShown,
  isBackgroundBlurred,
  hasCurrentTextSearch,
  isSeenByModalOpen,
  isReactorListModalOpen,
  isMessageLanguageModalOpen,
  isChannel,
  areChatSettingsLoaded,
  activeEmojiInteractions,
  lastSyncTime,
  pinnedIds,
  prevMiddleScreen,
  isBot,
  messageIds,
  messagesById,
  canPost,
  isComments,
  withBottomShift,
  withExtraShift,
  renderingShouldSendJoinRequest,
  isSelectModeActive,
  isPinnedMessageList,
  isMessagingDisabled,
  composerRestrictionMessage,
  renderingCanStartBot,
  renderingCanRestartBot,
  prevTransitionKey,
  currentTransitionKey,
  shouldSkipHistoryAnimations,
  animationLevel,
  handleOpenEnd,
  handleSlideStop,
}) => {
  const {
    openChat,
    openPreviousChat,
    unpinAllMessages,
    loadUser,
    loadChatSettings,
    joinChannel,
    sendBotCommand,
    restartBot,
    showNotification,
    init,
  } = getActions();

  const navigate = useNavigate();
  const { width: windowWidth } = useWindowSize();
  const { isTablet } = useAppLayout();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.language) === 'rtl';
  const [dropAreaState, setDropAreaState] = useState(DropAreaState.None);
  const [isFabShown, setIsFabShown] = useState<boolean | undefined>();
  const [isNotchShown, setIsNotchShown] = useState<boolean | undefined>();
  const [isUnpinModalOpen, setIsUnpinModalOpen] = useState(false);

  const isMobileSearchActive = isMobile && hasCurrentTextSearch;
  const closeAnimationDuration = isMobile
    ? LAYER_ANIMATION_DURATION_MS
    : undefined;
  const hasTools =
    hasPinned &&
    (windowWidth < MOBILE_SCREEN_MAX_WIDTH ||
      (isRightColumnShown &&
        windowWidth > MIN_SCREEN_WIDTH_FOR_STATIC_RIGHT_COLUMN &&
        windowWidth < SAFE_SCREEN_WIDTH_FOR_STATIC_RIGHT_COLUMN) ||
      (windowWidth >= MIN_SCREEN_WIDTH_FOR_STATIC_LEFT_COLUMN &&
        windowWidth < SAFE_SCREEN_WIDTH_FOR_CHAT_INFO &&
        hasAudioPlayer));

  const renderingHasTools = usePrevDuringAnimation(
    hasTools,
    closeAnimationDuration
  );
  const renderingIsFabShown =
    usePrevDuringAnimation(isFabShown, closeAnimationDuration) &&
    chatId !== TMP_CHAT_ID;

  const cleanupExceptionKey =
    prevTransitionKey !== undefined && prevTransitionKey < currentTransitionKey
      ? prevTransitionKey
      : undefined;

  useEffect(() => {
    return chatId
      ? captureEscKeyListener(() => {
          openChat({ id: undefined });
        })
      : undefined;
  }, [chatId, openChat]);

  useSyncEffect(() => {
    setDropAreaState(DropAreaState.None);
    setIsNotchShown(undefined);
  }, [chatId]);

  // Fix for mobile virtual keyboard
  useEffect(() => {
    const { visualViewport } = window;
    if (!visualViewport) {
      return undefined;
    }

    const handleResize = () => {
      if (visualViewport.height !== document.documentElement.clientHeight) {
        document.body.classList.add('keyboard-visible');
      } else {
        document.body.classList.remove('keyboard-visible');
      }
    };

    visualViewport.addEventListener('resize', handleResize);

    return () => {
      visualViewport.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isPrivate) {
      loadUser({ userId: chatId! });
    }
  }, [chatId, isPrivate, loadUser]);

  useEffect(() => {
    if (!areChatSettingsLoaded && lastSyncTime) {
      loadChatSettings({ chatId: chatId! });
    }
  }, [
    chatId,
    isPrivate,
    areChatSettingsLoaded,
    lastSyncTime,
    loadChatSettings,
  ]);

  useEffect(() => {
    if (!chatId) return;
    navigate(
      {
        pathname: `/${ERoutes.chat}/`,
        hash: `${chatId}`,
      },
      {
        state: {
          screens: {
            leftScreen: LeftColumnContent.ChatList,
            middleScreen: MiddleColumnContent.Messages,
          },
        },
      }
    );
  }, [chatId]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const { items } = e.dataTransfer || {};
    const shouldDrawQuick =
      items &&
      items.length > 0 &&
      Array.from(items)
        // Filter unnecessary element for drag and drop images in Firefox (https://github.com/Ajaxy/telegram-tt/issues/49)
        // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Recommended_drag_types#image
        .filter((item) => item.type !== 'text/uri-list')
        // As of September 2021, native clients suggest "send quick, but compressed" only for images
        .every(isImage);

    setDropAreaState(
      shouldDrawQuick ? DropAreaState.QuickFile : DropAreaState.Document
    );
  }, []);

  const handleHideDropArea = useCallback(() => {
    setDropAreaState(DropAreaState.None);
  }, []);

  const handleOpenUnpinModal = useCallback(() => {
    setIsUnpinModalOpen(true);
  }, []);

  const closeUnpinModal = useCallback(() => {
    setIsUnpinModalOpen(false);
  }, []);

  const handleUnpinAllMessages = useCallback(() => {
    unpinAllMessages({ chatId: chatId!, threadId: threadId! });
    closeUnpinModal();
    openPreviousChat();
  }, [unpinAllMessages, chatId, threadId, closeUnpinModal, openPreviousChat]);

  const handleTabletFocus = useCallback(() => {
    openChat({ id: chatId });
  }, [openChat, chatId]);

  const handleSubscribeClick = useCallback(() => {
    joinChannel({ chatId: chatId! });
    if (renderingShouldSendJoinRequest) {
      showNotification({
        message: isChannel
          ? t('RequestToJoinChannelSentDescription')
          : t('RequestToJoinGroupSentDescription'),
      });
    }
  }, [
    joinChannel,
    chatId,
    renderingShouldSendJoinRequest,
    showNotification,
    isChannel,
  ]);

  const handleStartBot = useCallback(() => {
    sendBotCommand({ command: '/start' });
  }, [sendBotCommand]);

  const handleRestartBot = useCallback(() => {
    restartBot({ chatId: chatId! });
  }, [chatId, restartBot]);

  const customBackgroundValue = useCustomBackground(theme, customBackground);

  const {
    onIntersectionChanged,
    onFocusPinnedMessage,
    getCurrentPinnedIndexes,
    getLoadingPinnedId,
    getForceNextPinnedInHeader,
  } = usePinnedMessage(chatId, threadId, pinnedIds);

  const className = classNames(
    'MiddleMessages',
    MASK_IMAGE_DISABLED ? 'mask-image-disabled' : 'mask-image-enabled',
    { 'has-header-tools': renderingHasTools }
  );

  const bgClassName = classNames(
    styles.background,
    styles.withTransition,
    customBackground && styles.customBgImage,
    backgroundColor && styles.customBgColor,
    customBackground && isBackgroundBlurred && styles.blurred,
    isRightColumnShown && styles.withRightColumn,
    chatId === AI_BOT_PHOENIX && styles.withPhoenix,
    chatId === AI_BOT_CANCER && styles.withCancer,
    chatId === AI_BOT_BUSINESS && styles.withBusiness
  );

  const messagingDisabledClassName = classNames('messaging-disabled', {
    shown: !isSelectModeActive,
  });

  // CSS Variables calculation doesn't work properly with transforms, so we calculate transform values in JS
  const {
    composerHiddenScale,
    toolbarHiddenScale,
    composerTranslateX,
    toolbarTranslateX,
    unpinHiddenScale,
    toolbarForUnpinHiddenScale,
  } = useMemo(
    () => calculateMiddleFooterTransforms(windowWidth, canPost),
    [canPost, windowWidth]
  );

  const footerClassName = classNames('middle-column-footer', {
    'no-composer': !canPost,
    'with-notch': canPost && isNotchShown && !isSelectModeActive,
  });

  // useHistoryBack({
  //   isActive: isSelectModeActive,
  //   onBack: exitMessageSelectMode,
  // });

  // useHistoryBack({
  //   isActive: isMobileSearchActive,
  //   onBack: closeLocalTextSearch,
  // });

  return (
    <div
      id='MiddleMessages'
      className={className}
      onTransitionEnd={handleOpenEnd}
      style={{
        //@ts-ignore
        '--composer-hidden-scale': composerHiddenScale,
        '--toolbar-hidden-scale': toolbarHiddenScale,
        '--unpin-hidden-scale': unpinHiddenScale,
        '--toolbar-unpin-hidden-scale': toolbarForUnpinHiddenScale,
        '--composer-translate-x': `${composerTranslateX}px`,
        '--toolbar-translate-x': `${toolbarTranslateX}px`,
        '--pattern-color': '#B2C6CC',
        '--theme-background-color': backgroundColor || undefined,
      }}
      onClick={isTablet && isLeftColumnShown ? handleTabletFocus : undefined}
    >
      <div
        className={bgClassName}
        style={{
          //@ts-ignore
          '--custom-background': customBackgroundValue
            ? customBackgroundValue
            : undefined,
        }}
      />
      <div id='middle-column-portals' />
      {chat && (
        <Helmet
          defaultTitle={chat?.title}
          titleTemplate={`${chat?.title} - %s`}
        />
      )}
      {Boolean(chatId && threadId) && (
        <>
          <div
            className={classNames('messages-layout', {
              'ai-bot': isBot,
              'ai-support': chatId === SUPPORT_USER_ID,
              'phoenix-bot': chatId === AI_BOT_PHOENIX,
            })}
            onDragEnter={canPost ? handleDragEnter : undefined}
          >
            <MiddleHeader
              chatId={chatId}
              threadId={threadId!}
              messageListType={renderingMessageListType}
              isReady={isReady}
              isMobile={isMobile}
              getCurrentPinnedIndexes={getCurrentPinnedIndexes}
              getLoadingPinnedId={getLoadingPinnedId}
              onFocusPinnedMessage={onFocusPinnedMessage}
              prevMiddleScreen={prevMiddleScreen}
            />
            <Transition
              name={
                shouldSkipHistoryAnimations
                  ? 'none'
                  : animationLevel === ANIMATION_LEVEL_MAX
                  ? 'slide'
                  : 'fade'
              }
              activeKey={currentTransitionKey}
              shouldCleanup
              cleanupExceptionKey={cleanupExceptionKey}
              onStop={handleSlideStop}
            >
              <MessageList
                key={`${chatId}-${threadId}-${renderingMessageListType}`}
                chatId={chatId}
                threadId={threadId!}
                isComments={isComments}
                messageIds={messageIds}
                messagesById={messagesById}
                type={renderingMessageListType}
                canPost={canPost}
                hasTools={renderingHasTools}
                onFabToggle={setIsFabShown}
                onNotchToggle={setIsNotchShown}
                isReady={isReady}
                withBottomShift={withBottomShift}
                withDefaultBg={Boolean(!customBackground && !backgroundColor)}
                onPinnedIntersectionChange={onIntersectionChanged}
                getForceNextPinnedInHeader={getForceNextPinnedInHeader}
              />
              <div className={footerClassName}>
                {canPost && (
                  <Composer
                    chatId={chatId!}
                    threadId={threadId!}
                    messageListType={renderingMessageListType!}
                    dropAreaState={dropAreaState}
                    onDropHide={handleHideDropArea}
                    isReady={isReady}
                    isMobile={isMobile}
                  />
                )}
                {isPinnedMessageList && (
                  <div
                    className='middle-column-footer-button-container'
                    dir={isRtl ? 'rtl' : undefined}
                  >
                    <Button
                      size='tiny'
                      fluid
                      className='unpin-all-button'
                      onClick={handleOpenUnpinModal}
                    >
                      <i className='icon-svg'>
                        <IconSvg name='unpin' />
                      </i>
                      <span>
                        {t('Chat.Pinned.UnpinAll', { pinnedMessagesCount })}
                      </span>
                    </Button>
                  </div>
                )}
                {isMessagingDisabled && (
                  <div className={messagingDisabledClassName}>
                    <div className='messaging-disabled-inner'>
                      <span>{composerRestrictionMessage}</span>
                    </div>
                  </div>
                )}
                {/* {isMobile &&
                  (renderingCanSubscribe ||
                    (renderingShouldJoinToSend &&
                      !renderingShouldSendJoinRequest)) && (
                    <div
                      className='middle-column-footer-button-container'
                      dir={isRtl ? 'rtl' : undefined}
                    >
                      <Button
                        size='tiny'
                        fluid
                        ripple
                        className='join-subscribe-button'
                        onClick={handleSubscribeClick}
                      >
                        {t(
                          renderingIsChannel
                            ? 'ProfileJoinChannel'
                            : 'ProfileJoinGroup'
                        )}
                      </Button>
                    </div>
                  )} */}
                {isMobile && renderingShouldSendJoinRequest && (
                  <div
                    className='middle-column-footer-button-container'
                    dir={isRtl ? 'rtl' : undefined}
                  >
                    <Button
                      size='tiny'
                      fluid
                      ripple
                      className='join-subscribe-button'
                      onClick={handleSubscribeClick}
                    >
                      {t('ChannelJoinRequest')}
                    </Button>
                  </div>
                )}
                {isMobile && renderingCanStartBot && (
                  <div
                    className='middle-column-footer-button-container'
                    dir={isRtl ? 'rtl' : undefined}
                  >
                    <Button
                      size='tiny'
                      fluid
                      ripple
                      className='join-subscribe-button'
                      onClick={handleStartBot}
                    >
                      {t('BotStart')}
                    </Button>
                  </div>
                )}
                {isMobile && renderingCanRestartBot && (
                  <div
                    className='middle-column-footer-button-container'
                    dir={isRtl ? 'rtl' : undefined}
                  >
                    <Button
                      size='tiny'
                      fluid
                      ripple
                      className='join-subscribe-button'
                      onClick={handleRestartBot}
                    >
                      {t('BotRestart')}
                    </Button>
                  </div>
                )}
                <MessageSelectToolbar
                  messageListType={renderingMessageListType}
                  isActive={isSelectModeActive}
                  canPost={canPost}
                />
                <SeenByModal isOpen={isSeenByModalOpen} />
                <ReactorListModal isOpen={isReactorListModalOpen} />
                <MessageLanguageModal isOpen={isMessageLanguageModalOpen} />
              </div>
            </Transition>

            <FloatingActionButtons
              isShown={renderingIsFabShown}
              canPost={canPost}
              withExtraShift={withExtraShift}
            />
          </div>
          {isMobile && (
            <MobileSearch isActive={Boolean(isMobileSearchActive)} />
          )}
        </>
      )}

      {/* {chatId && (
        <UnpinAllMessagesModal
          isOpen={isUnpinModalOpen}
          chatId={chatId}
          pinnedMessagesCount={pinnedMessagesCount}
          onClose={closeUnpinModal}
          onUnpin={handleUnpinAllMessages}
        />
      )} */}
      <div>
        {activeEmojiInteractions?.map((activeEmojiInteraction, i) => (
          <EmojiInteractionAnimation
            teactOrderKey={i}
            key={activeEmojiInteraction.id}
            activeEmojiInteraction={activeEmojiInteraction}
          />
        ))}
      </div>
      {/* <GiftPremiumModal isOpen={isGiftPremiumModalOpen} /> */}
    </div>
  );
};

export default memo(
  withGlobal<OwnProps>(
    (
      global,
      {
        chatId,
        isMobile,
        renderingMessageListType,
        threadId,
        isPinnedMessageList,
      }
    ): StateProps => {
      const theme = selectTheme(global);
      const {
        isBlurred: isBackgroundBlurred,
        background: customBackground,
        backgroundColor,
        patternColor,
      } = global.settings.themes[theme] || {};

      const {
        isLeftColumnShown,
        activeEmojiInteractions,
        seenByModal,
        giftPremiumModal,
        reactorModal,
        audioPlayer,
        messageLanguageModal,
      } = selectTabState(global);
      const currentMessageList = selectCurrentMessageList(global);

      const {
        chats: { listIds },
        lastSyncTime,
      } = global;

      const state: StateProps = {
        theme,
        customBackground,
        backgroundColor,
        patternColor,
        isLeftColumnShown,
        isRightColumnShown: selectIsRightColumnShown(global, isMobile),
        isBackgroundBlurred,
        hasCurrentTextSearch: Boolean(selectCurrentTextSearch(global)),
        isSeenByModalOpen: Boolean(seenByModal),
        isReactorListModalOpen: Boolean(reactorModal),
        isGiftPremiumModalOpen: giftPremiumModal?.isOpen,
        isMessageLanguageModalOpen: Boolean(messageLanguageModal),
        activeEmojiInteractions,
        lastSyncTime,
      };

      if (!currentMessageList || !listIds.active) {
        return state;
      }

      const { type: messageListType } = currentMessageList;

      const isPrivate = isUserId(chatId);
      const chat = selectChat(global, chatId);
      const bot = selectChatBot(global, chatId);
      const pinnedIds = selectPinnedIds(global, chatId, threadId!);
      const { chatId: audioChatId, messageId: audioMessageId } = audioPlayer;

      const isScheduledMessageList = messageListType === 'scheduled';
      const isChannel = Boolean(chat && isChatChannel(chat));

      const replyingToId = selectReplyingToId(global, chatId, threadId!);
      const audioMessage =
        audioChatId && audioMessageId
          ? selectChatMessage(global, audioChatId, audioMessageId)
          : undefined;

      const messageIds = selectCurrentMessageIds(
        global,
        chatId,
        threadId!,
        renderingMessageListType
      );

      const messagesById =
        renderingMessageListType === 'scheduled'
          ? selectChatScheduledMessages(global, chatId)
          : selectChatMessages(global, chatId);

      return {
        ...state,
        chat,
        replyingToId,
        isPrivate,
        areChatSettingsLoaded: Boolean(chat?.settings),
        isScheduledMessageList,
        hasPinned:
          (threadId !== MAIN_THREAD_ID && !chat?.isForum) ||
          Boolean(!isPinnedMessageList && pinnedIds?.length),
        hasAudioPlayer: Boolean(audioMessage),
        pinnedMessagesCount: pinnedIds ? pinnedIds.length : 0,
        isChannel,
        pinnedIds,
        isBot: Boolean(bot),
        messageIds,
        messagesById,
      };
    }
  )(MessagesLayout)
);
